import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { observer } from 'mobx-react-lite';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import Modal, { ModalProps } from 'shared/lib/components/modals/modal';
import { UploadedFileOrString } from 'shared/lib/utils/uploadedFileOrString';
import { UploadedReadedFile } from 'shared/lib/components/controls/uploadArea/uploadArea';
import UploadImageControl from 'shared/lib/components/controls/uploadImageControl';
import { useTranslation } from 'react-i18next';
import { emptySelectItemId } from 'shared/lib/utils/globalUtils';
import PlacementSelection from 'widgets/selectPlacement/placementSelection';
import { InitialPlacement } from 'entities/types';

export type EditFileModalApplyParams = {
  name: string;
  sectionId: string;
  folderId?: string;
  preview: UploadedFileOrString | null;
};

export default observer(function EditFileModal({
  initialFolderId,
  initialSectionId,
  fileName,
  preview,
  onSubmit,
  onDelete,
  applyText,
  headerText,
  handleClose,
  isOpen,
  sx,
}: InitialPlacement &
  ModalProps & {
    onSubmit: (params: EditFileModalApplyParams) => Promise<any>;
    onDelete: () => void;
    applyText?: string;
    headerText?: string;
    fileName: string;
    preview: UploadedFileOrString | null;
  }) {
  const { t } = useTranslation();
  applyText = applyText || t('common.save');
  headerText = headerText || t('common.edit');

  const {
    spacings: { sp },
  } = useContext(UiContext);

  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState(fileName);

  const [image, setImage] = useState(preview || null);

  const [selectedFolderId, setSelectedFolderId] = useState<string>(
    initialFolderId || ''
  );
  const [selectedSectionId, setSelectedSectionId] = useState<string>(
    initialSectionId || ''
  );

  const handleAddFile = useCallback((file: UploadedReadedFile) => {
    setImage(new UploadedFileOrString(file));
  }, []);

  const isDisabled = selectedSectionId === emptySelectItemId;

  const handleLoadingChange = (isLoading: boolean) => {
    setIsLoading(isLoading);
  };

  const finalIsDisabled = useMemo(() => {
    const changes =
      name !== fileName ||
      selectedFolderId !== (initialFolderId || '') ||
      selectedSectionId !== (initialSectionId || '') ||
      image?.src !== preview?.src;
    return !changes || isLoading || isDisabled || name.trim().length === 0;
  }, [
    name,
    fileName,
    selectedFolderId,
    initialFolderId,
    selectedSectionId,
    initialSectionId,
    image,
    preview,
    isLoading,
    isDisabled,
  ]);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp.s60 }}>
        <Typography variant="f24">{headerText}</Typography>
        <Box
          sx={{
            display: 'flex',
            position: 'relative',
            flexDirection: 'column',
            gap: sp.s40,
            overflow: 'hidden',
            ...sx,
          }}
        >
          <CustomTextField
            label={t('formFields.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            isClearIcon={true}
            labelTypographyVariantSize="f14"
            labelSxProps={{
              color: 'text.secondary',
            }}
          />
          <Paper
            elevation={4}
            sx={{
              display: 'flex',
              position: 'relative',
              flexDirection: 'column',
              overflow: 'hidden',
              width: 1,
              borderRadius: '16px',
              gap: sp.s40,
              p: sp.s60,
            }}
          >
            <PlacementSelection
              initialSectionId={selectedSectionId}
              initialFolderId={selectedFolderId || ''}
              onChangeFolder={(folderId) => setSelectedFolderId(folderId || '')}
              onChangeSection={(sectionId) => setSelectedSectionId(sectionId)}
              labelProps={{
                textFolder: t('folder.folder'),
                textSection: t('section.section'),
                sx: { color: 'text.secondary' },
              }}
              onLoadingChange={handleLoadingChange}
            />
          </Paper>
          <UploadImageControl
            image={image}
            onRemove={() => setImage(null)}
            onUpload={handleAddFile}
            fieldName={t('noCategory.preview')}
          ></UploadImageControl>
        </Box>

        <ButtonsContainer variant="vertical" sx={{ width: 1 }}>
          <ButtonsContainer>
            <SecondaryButton onClick={handleClose}>
              {t('common.cancel')}
            </SecondaryButton>
            <PrimaryButton
              loading={isLoading}
              disabled={finalIsDisabled}
              onClick={async () => {
                setIsLoading(true);

                try {
                  await onSubmit({
                    folderId: selectedFolderId,
                    sectionId: selectedSectionId,
                    name,
                    preview: image,
                  });
                } catch (e) {
                  console.error(e);
                }
                setIsLoading(false);
              }}
            >
              {applyText}
            </PrimaryButton>
          </ButtonsContainer>

          {onDelete ? (
            <PrimaryButton
              onClick={() => onDelete()}
              variant="contained"
              color="error"
            >
              {t('common.delete')}
            </PrimaryButton>
          ) : (
            ''
          )}
        </ButtonsContainer>
      </Box>
    </Modal>
  );
});
